import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";

const statusLabels = {
  scheduled: "Programado",
  registration_open: "Abiertos",
  live: "En vivo",
  active: "En vivo",
  completed: "Finalizado",
  cancelled: "Cancelado"
};

const statusVariants = {
  scheduled: "light",
  registration_open: "warning",
  live: "success",
  active: "success",
  completed: "secondary",
  cancelled: "dark"
};

function TournamentsHome() {
  const [tournaments, setTournaments] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [pendingUnregisterId, setPendingUnregisterId] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");

  const loadTournaments = useCallback(() => {
    setLoading(true);
    api
      .get("/tournaments", { params: { search: query || undefined, status } })
      .then(({ data }) => setTournaments(data || []))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, [query, status]);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);


  const registerTournament = async tournament => {
    setNotice("");
    if (!localStorage.getItem("token")) {
      setNotice("Necesitas iniciar sesion para confirmar asistencia.");
      return;
    }

    try {
      const { data } = await api.post(`/tournaments/${tournament.id}/register`);
      setNotice(data.status === "waitlisted"
        ? `Te sumamos a la lista de espera en la posicion ${data.waitlist_position}.`
        : "Asistencia registrada.");
      setPendingUnregisterId(null);
      loadTournaments();
    } catch (error) {
      setNotice("No pudimos confirmar asistencia. Puede que el evento este cerrado o sin cupo.");
    }
  };

  const unregisterTournament = async tournament => {
    setNotice("");
    await api.delete(`/tournaments/${tournament.id}/register`);
    setNotice("Cancelaste tu asistencia al evento.");
    setPendingUnregisterId(null);
    loadTournaments();
  };

  const metrics = useMemo(() => ({
    total: tournaments.length,
    live: tournaments.filter(tournament => ["live", "active"].includes(tournament.status)).length,
    upcoming: tournaments.filter(tournament => ["scheduled", "registration_open"].includes(tournament.status)).length,
    players: tournaments.reduce((sum, tournament) => sum + Number(tournament.players_count || 0), 0)
  }), [tournaments]);

  const formatPrice = tournament => {
    if (!tournament.entry_fee_cents) return "Gratis/consultar";
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR"
    }).format(tournament.entry_fee_cents / 100);
  };


  const canRegisterTournament = tournament => ["scheduled", "registration_open"].includes(tournament.status);

  return (
    <Container className="page-band tournaments-page">
      <div className="community-directory-header mb-4">
        <div>
          <h1 className="section-title mb-1">Eventos</h1>
          <p className="muted-copy mb-0">Eventos de tiendas para confirmar asistencia y descubrir cartas/personas afines.</p>
        </div>
        <Button as={Link} to="/communities" variant="outline-dark">
          Ver comunidades
        </Button>
      </div>

      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}><div className="store-metric"><span>Eventos</span><strong>{metrics.total}</strong></div></Col>
        <Col sm={6} xl={3}><div className="store-metric"><span>Activos</span><strong>{metrics.live}</strong></div></Col>
        <Col sm={6} xl={3}><div className="store-metric"><span>Proximos</span><strong>{metrics.upcoming}</strong></div></Col>
        <Col sm={6} xl={3}><div className="store-metric"><span>Asistentes</span><strong>{metrics.players}</strong></div></Col>
      </Row>

      <Card className="cardora-card mb-4">
        <Card.Body>
          <Row className="g-2">
            <Col md={8}>
              <InputGroup>
                <Form.Control
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Buscar evento, tienda o ciudad..."
                />
                <Button variant="outline-dark" onClick={() => setQuery("")}>
                  Limpiar
                </Button>
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Select value={status} onChange={event => setStatus(event.target.value)}>
                <option value="all">Todos los estados</option>
                <option value="registration_open">Abiertos</option>
                <option value="scheduled">Programados</option>
                <option value="live">Activos</option>
                <option value="completed">Finalizados</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {notice && <div className="inline-page-notice mb-3">{notice}</div>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Row className="g-3">
          {tournaments.length === 0 && (
            <Col>
              <Card className="cardora-card">
                <Card.Body>No encontramos eventos con ese criterio.</Card.Body>
              </Card>
            </Col>
          )}
          {tournaments.map(tournament => {
            const isRegistered = Boolean(tournament.viewer_is_registered) || tournament.players?.some(player => player.user_id === currentUser?.id);
            const isWaitlisted = Boolean(tournament.viewer_is_waitlisted);
            const canSelfManage = !["completed", "cancelled"].includes(tournament.status);
            const canRegister = canRegisterTournament(tournament);
            const isFull = tournament.capacity && Number(tournament.players_count || 0) >= Number(tournament.capacity);

            return (
            <Col md={6} xl={4} key={tournament.id}>
              <Card className="cardora-card h-100 tournament-card">
                <Card.Body>
                  <div className="tournament-card-store">
                    {tournament.business_logo_url ? (
                      <img src={mediaUrl(tournament.business_logo_url)} alt="" />
                    ) : (
                      <span>{tournament.business_name?.slice(0, 1) || "C"}</span>
                    )}
                    <div>
                      <strong>{tournament.business_name}</strong>
                      <small>{tournament.business_city || tournament.business_slug}</small>
                    </div>
                    {tournament.business_is_verified && <Badge bg="success">Verificada</Badge>}
                  </div>

                  <div className="d-flex justify-content-between align-items-start gap-2 mt-3">
                    <Card.Title>{tournament.name}</Card.Title>
                    <Badge bg={statusVariants[tournament.status] || "light"} text={["scheduled"].includes(tournament.status) ? "dark" : undefined}>
                      {statusLabels[tournament.status] || tournament.status}
                    </Badge>
                  </div>
                  <Card.Text className="muted-copy">{tournament.description || "Evento de comunidad Cardora."}</Card.Text>

                  <div className="tournament-facts">
                    <span>{tournament.starts_at ? new Date(tournament.starts_at).toLocaleString("es-ES") : "Fecha por confirmar"}</span>
                    <span>{tournament.players_count || 0}/{tournament.capacity || "sin limite"} asistentes</span>
                    <span>{formatPrice(tournament)}</span>
                  </div>

                  {tournament.players?.length > 0 && (
                    <div className="tournament-player-list mt-3">
                      {tournament.players.slice(0, 6).map(player => (
                        <Link to={`/u/${player.handle || player.user_id}`} key={player.user_id}>
                          {player.avatar_url ? <img src={mediaUrl(player.avatar_url)} alt="" /> : <span>{player.display_name?.slice(0, 1) || "C"}</span>}
                          <small>{player.display_name}</small>
                        </Link>
                      ))}
                      {tournament.players.length > 6 && <span className="muted-copy small">+{tournament.players.length - 6}</span>}
                    </div>
                  )}

                  <div className="community-store-actions mt-3">
                    {!localStorage.getItem("token") ? (
                      <Button as={Link} to="/register" className="btn-cardora">
                        Registrarme para asistir
                      </Button>
                    ) : isRegistered || isWaitlisted ? (
                      pendingUnregisterId === tournament.id ? (
                        <div className="inline-confirm-actions">
                          <span>Confirmar baja?</span>
                          <Button size="sm" variant="outline-danger" onClick={() => unregisterTournament(tournament)}>
                            Darme de baja
                          </Button>
                          <Button size="sm" variant="outline-dark" onClick={() => setPendingUnregisterId(null)}>
                            Cancelar
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Button variant={isWaitlisted ? "outline-warning" : "outline-success"} disabled>
                            {isWaitlisted ? `Lista de espera #${tournament.viewer_waitlist_position || "-"}` : "Asistencia confirmada"}
                          </Button>
                          {canSelfManage && (
                            <Button variant="outline-danger" onClick={() => setPendingUnregisterId(tournament.id)}>
                              Darme de baja
                            </Button>
                          )}
                        </>
                      )
                    ) : canRegister ? (
                      <Button className="btn-cardora" onClick={() => registerTournament(tournament)}>
                        {isFull ? "Lista de espera" : "Confirmar asistencia"}
                      </Button>
                    ) : (
                      <Button variant="outline-dark" disabled>
                        Asistencia cerrada
                      </Button>
                    )}
                    <Button as={Link} to={`/store/${tournament.business_slug}`} className="btn-cardora">
                      Ver tienda
                    </Button>
                    <Button as={Link} to={`/tournaments/${tournament.id}`} variant="outline-dark">
                      Ver evento
                    </Button>
                    {tournament.external_ranking_url && (
                      <Button as="a" href={tournament.external_ranking_url} target="_blank" rel="noreferrer" variant="outline-dark">
                        Info externa
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            );
          })}
        </Row>
      )}
    </Container>
  );
}

export default TournamentsHome;
